import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddPhotoAlternate as AddPhotoIcon,
  Code as CodeIcon,
  DeleteOutline as DeleteIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatQuote as QuoteIcon,
  FormatStrikethrough as StrikeIcon,
  FormatUnderlined as UnderlineIcon,
  Link as LinkIcon,
  NewReleases as SpoilerIcon,
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/common/Page';
import { useAdminI18n } from '@/contexts/AdminI18nContext';
import { useAdminTime } from '@/contexts/AdminTimeContext';
import {
  type BotPostInput,
  createBotPost,
  deleteBotPostMessages,
  fetchBotPostMedia,
  getAdminBotPosts,
  getAdminUsers,
  sendBotPost,
  updateBotPost,
  uploadBotPostMedia,
} from '@/services/api';
import type { AdminUser, BotPost, BotPostStatus, BotPostTargetAudience } from '@/types';

type CropMode = 'square' | 'portrait' | 'wide' | 'original';
type BotPostMode = 'broadcast' | 'user';
type UserPickerOption = Pick<AdminUser, 'id' | 'telegram_id' | 'telegram_username' | 'first_name' | 'last_name'> & {
  label?: string | null;
};

type ComposerState = BotPostInput & {
  id: number | null;
  media_preview_url: string | null;
  include_button: boolean;
};

type ProcessedImage = {
  file: File;
  width: number;
  height: number;
  previewUrl: string;
};

const CROP_RATIOS: Record<CropMode, number | null> = {
  square: 1,
  portrait: 4 / 5,
  wide: 16 / 9,
  original: null,
};

const STATUS_COLORS: Record<BotPostStatus, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  draft: 'warning',
  sending: 'info',
  sent: 'success',
  failed: 'error',
  deleted: 'default',
};

const DEFAULT_MESSAGE = [
  '⚡ <b>Market update</b>',
  '',
  'Coin: ',
  'Route: ',
  'Spread: ',
  '',
  '🔥 Watch the scanner for entries.',
].join('\n');

function createEmptyForm(mode: BotPostMode = 'broadcast'): ComposerState {
  return {
    id: null,
    title: '',
    body_html: DEFAULT_MESSAGE,
    media_key: null,
    media_file_name: null,
    media_content_type: null,
    media_size: null,
    media_width: null,
    media_height: null,
    target_audience: mode === 'user' ? 'user' : 'all',
    target_user_id: null,
    button_text: '',
    button_url: '',
    disable_notification: false,
    protect_content: false,
    show_caption_above_media: true,
    has_media_spoiler: false,
    media_preview_url: null,
    include_button: true,
  };
}

function formatAdminUserLabel(user: UserPickerOption) {
  if (user.label) return user.label;
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  const handle = user.telegram_username ? `@${user.telegram_username}` : user.telegram_id;
  return name ? `${name} (${handle})` : handle;
}

function revokePreviewUrl(url: string | null | undefined) {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

function stripTelegramHtml(html: string) {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function sanitizeTelegramPreviewHtml(html: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const allowed = new Set([
    'a',
    'b',
    'blockquote',
    'code',
    'del',
    'em',
    'i',
    'ins',
    'pre',
    's',
    'span',
    'strike',
    'strong',
    'tg-spoiler',
    'u',
  ]);

  const sanitizeNode = (node: ChildNode): string => {
    if (node.nodeType === Node.TEXT_NODE) return escapeHtml(node.textContent || '');
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();
    const children = Array.from(element.childNodes).map(sanitizeNode).join('');
    if (!allowed.has(tag)) return children;

    if (tag === 'a') {
      const href = element.getAttribute('href') || '';
      const safeHref = /^https?:\/\//i.test(href) ? href : '#';
      return `<a href="${escapeHtml(safeHref)}" target="_blank" rel="noreferrer">${children}</a>`;
    }

    if (tag === 'tg-spoiler' || (tag === 'span' && element.className === 'tg-spoiler')) {
      return `<span class="tg-spoiler">${children}</span>`;
    }

    return `<${tag}>${children}</${tag}>`;
  };

  return Array.from(doc.body.firstElementChild?.childNodes || []).map(sanitizeNode).join('');
}

function loadImage(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    image.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Failed to process image'));
    }, 'image/jpeg', 0.9);
  });
}

async function processImageFile(file: File, cropMode: CropMode): Promise<ProcessedImage> {
  const image = await loadImage(file);
  const targetRatio = CROP_RATIOS[cropMode];
  let sourceX = 0;
  let sourceY = 0;
  let sourceWidth = image.naturalWidth;
  let sourceHeight = image.naturalHeight;

  if (targetRatio) {
    const sourceRatio = sourceWidth / sourceHeight;
    if (sourceRatio > targetRatio) {
      sourceWidth = sourceHeight * targetRatio;
      sourceX = (image.naturalWidth - sourceWidth) / 2;
    } else {
      sourceHeight = sourceWidth / targetRatio;
      sourceY = (image.naturalHeight - sourceHeight) / 2;
    }
  }

  const maxWidth = targetRatio === CROP_RATIOS.portrait ? 1080 : 1400;
  const scale = Math.min(1, maxWidth / sourceWidth);
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas is not available');

  context.fillStyle = '#101715';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, width, height);

  const blob = await canvasToBlob(canvas);
  const baseName = file.name.replace(/\.[^.]+$/, '') || 'bot-post-image';
  const outputFile = new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });

  return {
    file: outputFile,
    width,
    height,
    previewUrl: URL.createObjectURL(blob),
  };
}

function BotPostMediaImage({ mediaKey, alt }: { mediaKey: string; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    fetchBotPostMedia(mediaKey)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });

    return () => {
      cancelled = true;
      revokePreviewUrl(objectUrl);
    };
  }, [mediaKey]);

  if (!url) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 180 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={url}
      alt={alt}
      sx={{ width: '100%', display: 'block', objectFit: 'cover' }}
    />
  );
}

export default function AdminBotPosts() {
  const queryClient = useQueryClient();
  const { t } = useAdminI18n();
  const { formatDateTime } = useAdminTime();
  const messageRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [form, setForm] = useState<ComposerState>(() => createEmptyForm());
  const [activeMode, setActiveMode] = useState<BotPostMode>('broadcast');
  const [selectedUser, setSelectedUser] = useState<UserPickerOption | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [cropMode, setCropMode] = useState<CropMode>('square');
  const [sourceImageFile, setSourceImageFile] = useState<File | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-bot-posts'],
    queryFn: getAdminBotPosts,
  });

  const { data: usersData, isLoading: isUsersLoading } = useQuery({
    queryKey: ['admin-bot-post-user-picker', userSearch],
    queryFn: () => getAdminUsers(50, 0, userSearch || undefined),
    enabled: activeMode === 'user',
  });

  useEffect(() => () => revokePreviewUrl(form.media_preview_url), [form.media_preview_url]);

  const posts = data?.posts ?? [];
  const visiblePosts = useMemo(
    () => posts.filter((post) => activeMode === 'user' ? post.target_audience === 'user' : post.target_audience !== 'user'),
    [activeMode, posts],
  );
  const userOptions = useMemo(() => {
    const users = usersData?.users ?? [];
    if (!selectedUser) return users;
    return users.some((user) => user.id === selectedUser.id) ? users : [selectedUser, ...users];
  }, [selectedUser, usersData?.users]);
  const plainLength = stripTelegramHtml(form.body_html).length;
  const messageLimit = form.media_key ? 1024 : 4096;
  const overLimit = plainLength > messageLimit;
  const previewHtml = useMemo(() => sanitizeTelegramPreviewHtml(form.body_html), [form.body_html]);

  const uploadMutation = useMutation({
    mutationFn: async (image: ProcessedImage) => {
      const response = await uploadBotPostMedia(image.file, { width: image.width, height: image.height });
      return { ...response.media, previewUrl: image.previewUrl };
    },
    onSuccess: (media) => {
      setForm((current) => {
        revokePreviewUrl(current.media_preview_url);
        return {
          ...current,
          media_key: media.key,
          media_file_name: media.file_name,
          media_content_type: media.content_type,
          media_size: media.size,
          media_width: media.width,
          media_height: media.height,
          media_preview_url: media.previewUrl,
        };
      });
    },
    onError: (mutationError) => {
      setStatusMessage(mutationError instanceof Error ? mutationError.message : t('admin.botPosts.imageUploadFailed'));
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload();
      return form.id ? updateBotPost(form.id, payload) : createBotPost(payload);
    },
    onSuccess: (response) => {
      setForm((current) => ({ ...current, id: response.post.id }));
      setStatusMessage(t('admin.botPosts.saved'));
      queryClient.invalidateQueries({ queryKey: ['admin-bot-posts'] });
    },
  });

  const sendMutation = useMutation({
    mutationFn: async (postId?: number) => {
      let id = postId ?? null;
      if (!id) {
        const saved = form.id
          ? await updateBotPost(form.id, buildPayload())
          : await createBotPost(buildPayload());
        id = saved.post.id;
        setForm((current) => ({ ...current, id: saved.post.id }));
      }

      return sendBotPost(id);
    },
    onSuccess: (response) => {
      setStatusMessage(t('admin.botPosts.sentSummary', response.summary));
      setForm(createEmptyForm(activeMode));
      if (activeMode === 'user') setSelectedUser(null);
      setSourceImageFile(null);
      queryClient.invalidateQueries({ queryKey: ['admin-bot-posts'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteBotPostMessages,
    onSuccess: (response) => {
      setStatusMessage(t('admin.botPosts.deleteSummary', response.summary));
      queryClient.invalidateQueries({ queryKey: ['admin-bot-posts'] });
    },
  });

  const actionError = saveMutation.error || sendMutation.error || deleteMutation.error;

  const buildPayload = (): BotPostInput => ({
    title: form.title,
    body_html: form.body_html,
    media_key: form.media_key,
    media_file_name: form.media_file_name,
    media_content_type: form.media_content_type,
    media_size: form.media_size,
    media_width: form.media_width,
    media_height: form.media_height,
    target_audience: activeMode === 'user' ? 'user' : form.target_audience,
    target_user_id: activeMode === 'user' ? form.target_user_id : null,
    button_text: form.include_button ? form.button_text || t('admin.botPosts.defaultButtonText') : null,
    button_url: form.include_button ? form.button_url || null : null,
    disable_notification: form.disable_notification,
    protect_content: form.protect_content,
    show_caption_above_media: form.show_caption_above_media,
    has_media_spoiler: form.has_media_spoiler,
  });

  const canSubmit = Boolean(form.title.trim())
    && Boolean(form.body_html.trim() || form.media_key)
    && (activeMode !== 'user' || Boolean(form.target_user_id))
    && !overLimit
    && !uploadMutation.isPending;

  const updateBody = (nextBody: string) => {
    setForm((current) => ({ ...current, body_html: nextBody }));
  };

  const insertAroundSelection = (before: string, after: string = before) => {
    const textarea = messageRef.current;
    const value = form.body_html;
    if (!textarea) {
      updateBody(`${value}${before}${after}`);
      return;
    }

    const start = textarea.selectionStart ?? value.length;
    const end = textarea.selectionEnd ?? value.length;
    const selection = value.slice(start, end);
    const nextValue = `${value.slice(0, start)}${before}${selection || ''}${after}${value.slice(end)}`;
    updateBody(nextValue);

    window.requestAnimationFrame(() => {
      textarea.focus();
      const cursor = selection ? start + before.length + selection.length + after.length : start + before.length;
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const insertSnippet = (snippet: string) => {
    const textarea = messageRef.current;
    const value = form.body_html;
    const start = textarea?.selectionStart ?? value.length;
    const end = textarea?.selectionEnd ?? value.length;
    const nextValue = `${value.slice(0, start)}${snippet}${value.slice(end)}`;
    updateBody(nextValue);

    window.requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + snippet.length, start + snippet.length);
    });
  };

  const handleImageFile = async (file: File, mode = cropMode) => {
    setStatusMessage(null);
    setSourceImageFile(file);

    try {
      const processed = await processImageFile(file, mode);
      uploadMutation.mutate(processed);
    } catch (processError) {
      setStatusMessage(processError instanceof Error ? processError.message : t('admin.botPosts.imageProcessingFailed'));
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleImageFile(file);
    }
  };

  const handleCropChange = (_: unknown, nextMode: CropMode | null) => {
    if (!nextMode) return;
    setCropMode(nextMode);
    if (sourceImageFile) {
      handleImageFile(sourceImageFile, nextMode);
    }
  };

  const handleRemoveImage = () => {
    setSourceImageFile(null);
    setForm((current) => {
      revokePreviewUrl(current.media_preview_url);
      return {
        ...current,
        media_key: null,
        media_file_name: null,
        media_content_type: null,
        media_size: null,
        media_width: null,
        media_height: null,
        media_preview_url: null,
        has_media_spoiler: false,
      };
    });
  };

  const handleNewPost = (mode = activeMode) => {
    setSourceImageFile(null);
    if (mode !== 'user') setSelectedUser(null);
    setForm((current) => {
      revokePreviewUrl(current.media_preview_url);
      return createEmptyForm(mode);
    });
    setStatusMessage(null);
  };

  const handleModeChange = (_: unknown, nextMode: BotPostMode) => {
    if (!nextMode || nextMode === activeMode) return;
    setActiveMode(nextMode);
    setSelectedUser(null);
    setUserSearch('');
    handleNewPost(nextMode);
  };

  const loadPostIntoComposer = (post: BotPost) => {
    const mode: BotPostMode = post.target_audience === 'user' ? 'user' : 'broadcast';
    setActiveMode(mode);
    setSourceImageFile(null);
    setStatusMessage(null);
    setSelectedUser(
      post.target_audience === 'user' && post.target_user_id
        ? {
            id: post.target_user_id,
            telegram_id: post.target_user_label || String(post.target_user_id),
            telegram_username: null,
            first_name: null,
            last_name: null,
            label: post.target_user_label,
          }
        : null,
    );
    setForm((current) => {
      revokePreviewUrl(current.media_preview_url);
      return {
        id: post.status === 'draft' ? post.id : null,
        title: post.status === 'draft' ? post.title : t('admin.botPosts.copyTitle', { title: post.title }),
        body_html: post.body_html,
        media_key: post.media_key,
        media_file_name: post.media_file_name,
        media_content_type: post.media_content_type,
        media_size: post.media_size,
        media_width: post.media_width,
        media_height: post.media_height,
        target_audience: post.target_audience,
        target_user_id: post.target_user_id,
        button_text: post.button_text || t('admin.botPosts.defaultButtonText'),
        button_url: post.button_url || '',
        disable_notification: Boolean(post.disable_notification),
        protect_content: Boolean(post.protect_content),
        show_caption_above_media: Boolean(post.show_caption_above_media),
        has_media_spoiler: Boolean(post.has_media_spoiler),
        media_preview_url: null,
        include_button: Boolean(post.button_text),
      };
    });
  };

  const statusLabel = (status: BotPostStatus) => {
    switch (status) {
      case 'sending':
        return t('admin.botPosts.statusSending');
      case 'sent':
        return t('admin.botPosts.statusSent');
      case 'failed':
        return t('admin.botPosts.statusFailed');
      case 'deleted':
        return t('admin.botPosts.statusDeleted');
      default:
        return t('admin.botPosts.statusDraft');
    }
  };

  const audienceLabel = (audience: BotPostTargetAudience) => {
    if (audience === 'user') return t('admin.botPosts.targetUser');
    if (audience === 'basic') return t('admin.botPosts.targetBasic');
    if (audience === 'private') return t('admin.botPosts.targetPrivate');
    return t('admin.botPosts.targetAll');
  };

  const formAudienceLabel = () => {
    if (activeMode === 'user') {
      return selectedUser ? formatAdminUserLabel(selectedUser) : t('admin.botPosts.targetUser');
    }
    return audienceLabel(form.target_audience);
  };

  const postAudienceLabel = (post: BotPost) => (
    post.target_audience === 'user'
      ? post.target_user_label || t('admin.botPosts.targetUser')
      : audienceLabel(post.target_audience)
  );

  return (
    <Box>
      <SectionHeader
        title={t('admin.botPosts.title')}
        detail={t('admin.botPosts.detail')}
        action={
          <Button variant="outlined" startIcon={<ResetIcon />} onClick={() => handleNewPost()}>
            {t('admin.botPosts.newPost')}
          </Button>
        }
      />

      {statusMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {statusMessage}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError instanceof Error
            ? actionError.message
            : t('admin.botPosts.actionFailed')}
        </Alert>
      )}

      <Tabs
        value={activeMode}
        onChange={handleModeChange}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="broadcast" label={t('admin.botPosts.broadcastPosts')} />
        <Tab value="user" label={t('admin.botPosts.userMessages')} />
      </Tabs>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6">{t('admin.botPosts.composer')}</Typography>

                <TextField
                  label={t('admin.botPosts.titleField')}
                  value={form.title}
                  onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                  fullWidth
                />

                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  <Tooltip title={t('admin.botPosts.formatBold')}>
                    <IconButton size="small" onClick={() => insertAroundSelection('<b>', '</b>')}>
                      <BoldIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('admin.botPosts.formatItalic')}>
                    <IconButton size="small" onClick={() => insertAroundSelection('<i>', '</i>')}>
                      <ItalicIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('admin.botPosts.formatUnderline')}>
                    <IconButton size="small" onClick={() => insertAroundSelection('<u>', '</u>')}>
                      <UnderlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('admin.botPosts.formatStrike')}>
                    <IconButton size="small" onClick={() => insertAroundSelection('<s>', '</s>')}>
                      <StrikeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('admin.botPosts.formatCode')}>
                    <IconButton size="small" onClick={() => insertAroundSelection('<code>', '</code>')}>
                      <CodeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('admin.botPosts.formatQuote')}>
                    <IconButton size="small" onClick={() => insertAroundSelection('<blockquote>', '</blockquote>')}>
                      <QuoteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('admin.botPosts.formatSpoiler')}>
                    <IconButton size="small" onClick={() => insertAroundSelection('<tg-spoiler>', '</tg-spoiler>')}>
                      <SpoilerIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('admin.botPosts.formatLink')}>
                    <IconButton size="small" onClick={() => insertAroundSelection('<a href="https://">', '</a>')}>
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {['⚡', '💰', '📍', '🔥', '🚀'].map((emoji) => (
                    <Button key={emoji} size="small" variant="text" onClick={() => insertSnippet(emoji)}>
                      {emoji}
                    </Button>
                  ))}
                </Stack>

                <TextField
                  label={t('admin.botPosts.message')}
                  value={form.body_html}
                  onChange={(event) => setForm((current) => ({ ...current, body_html: event.target.value }))}
                  inputRef={messageRef}
                  multiline
                  minRows={10}
                  fullWidth
                  error={overLimit}
                  helperText={
                    form.media_key
                      ? `${t('admin.botPosts.characters', { count: plainLength, limit: messageLimit })} - ${t('admin.botPosts.captionLimit')}`
                      : t('admin.botPosts.characters', { count: plainLength, limit: messageLimit })
                  }
                />

                <Stack spacing={1}>
                  <Typography variant="subtitle2">{t('admin.botPosts.uploadImage')}</Typography>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    hidden
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) handleImageFile(file);
                      event.target.value = '';
                    }}
                  />
                  <Box
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      minHeight: 112,
                      display: 'grid',
                      placeItems: 'center',
                      cursor: 'pointer',
                      borderRadius: 2,
                      border: '1px dashed rgba(118, 244, 184, 0.42)',
                      bgcolor: 'rgba(118, 244, 184, 0.06)',
                    }}
                  >
                    {uploadMutation.isPending ? (
                      <CircularProgress size={24} />
                    ) : (
                      <Stack alignItems="center" spacing={0.75}>
                        <AddPhotoIcon color="primary" />
                        <Typography variant="body2" color="text.secondary">
                          {t('admin.botPosts.dragImage')}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('admin.botPosts.crop')}
                    </Typography>
                    <ToggleButtonGroup value={cropMode} exclusive size="small" onChange={handleCropChange}>
                      <ToggleButton value="square">{t('admin.botPosts.cropSquare')}</ToggleButton>
                      <ToggleButton value="portrait">{t('admin.botPosts.cropPortrait')}</ToggleButton>
                      <ToggleButton value="wide">{t('admin.botPosts.cropWide')}</ToggleButton>
                      <ToggleButton value="original">{t('admin.botPosts.cropOriginal')}</ToggleButton>
                    </ToggleButtonGroup>
                    {form.media_key && (
                      <Button size="small" color="error" onClick={handleRemoveImage}>
                        {t('admin.botPosts.removeImage')}
                      </Button>
                    )}
                  </Stack>
                </Stack>

                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    {activeMode === 'user' ? (
                      <Autocomplete
                        options={userOptions}
                        value={selectedUser}
                        loading={isUsersLoading}
                        inputValue={userSearch}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={formatAdminUserLabel}
                        onInputChange={(_, value) => setUserSearch(value)}
                        onChange={(_, user) => {
                          setSelectedUser(user);
                          setForm((current) => ({
                            ...current,
                            target_audience: 'user',
                            target_user_id: user?.id ?? null,
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('admin.botPosts.userPicker')}
                            placeholder={t('admin.botPosts.userPickerPlaceholder')}
                            error={!form.target_user_id}
                            helperText={!form.target_user_id ? t('admin.botPosts.userRequired') : ' '}
                          />
                        )}
                      />
                    ) : (
                      <FormControl fullWidth>
                        <InputLabel>{t('admin.botPosts.targetAudience')}</InputLabel>
                        <Select
                          label={t('admin.botPosts.targetAudience')}
                          value={form.target_audience}
                          onChange={(event) =>
                            setForm((current) => ({
                              ...current,
                              target_audience: event.target.value as BotPostTargetAudience,
                              target_user_id: null,
                            }))
                          }
                        >
                          <MenuItem value="all">{t('admin.botPosts.targetAll')}</MenuItem>
                          <MenuItem value="basic">{t('admin.botPosts.targetBasic')}</MenuItem>
                          <MenuItem value="private">{t('admin.botPosts.targetPrivate')}</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.include_button}
                          onChange={(event) => setForm((current) => ({ ...current, include_button: event.target.checked }))}
                        />
                      }
                      label={t('admin.botPosts.includeButton')}
                    />
                  </Grid>
                </Grid>

                {form.include_button && (
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        label={t('admin.botPosts.buttonText')}
                        value={form.button_text || ''}
                        onChange={(event) => setForm((current) => ({ ...current, button_text: event.target.value }))}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                      <TextField
                        label={t('admin.botPosts.buttonUrl')}
                        value={form.button_url || ''}
                        onChange={(event) => setForm((current) => ({ ...current, button_url: event.target.value }))}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                )}

                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(form.disable_notification)}
                        onChange={(event) => setForm((current) => ({ ...current, disable_notification: event.target.checked }))}
                      />
                    }
                    label={t('admin.botPosts.silent')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(form.protect_content)}
                        onChange={(event) => setForm((current) => ({ ...current, protect_content: event.target.checked }))}
                      />
                    }
                    label={t('admin.botPosts.protectContent')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(form.has_media_spoiler)}
                        disabled={!form.media_key}
                        onChange={(event) => setForm((current) => ({ ...current, has_media_spoiler: event.target.checked }))}
                      />
                    }
                    label={t('admin.botPosts.mediaSpoiler')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(form.show_caption_above_media)}
                        disabled={!form.media_key}
                        onChange={(event) => setForm((current) => ({ ...current, show_caption_above_media: event.target.checked }))}
                      />
                    }
                    label={t('admin.botPosts.captionAbove')}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    disabled={!canSubmit || saveMutation.isPending}
                    onClick={() => saveMutation.mutate()}
                  >
                    {form.id ? t('admin.botPosts.updateDraft') : t('admin.botPosts.saveDraft')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={sendMutation.isPending ? <CircularProgress size={18} /> : <SendIcon />}
                    disabled={!canSubmit || sendMutation.isPending}
                    onClick={() => {
                      if (window.confirm(t('admin.botPosts.confirmSend', { audience: formAudienceLabel() }))) {
                        sendMutation.mutate(undefined);
                      }
                    }}
                  >
                    {sendMutation.isPending ? t('admin.botPosts.sending') : t('admin.botPosts.sendNow')}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Stack spacing={2}>
            <Card>
              <CardContent>
                <Stack spacing={1.5}>
                  <Typography variant="h6">{t('admin.botPosts.preview')}</Typography>
                  <Box
                    sx={{
                      maxWidth: 420,
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: '#172536',
                      border: '1px solid rgba(226, 255, 238, 0.12)',
                    }}
                  >
                    {form.media_key && form.show_caption_above_media && (
                      <Box
                        sx={{
                          p: 1.5,
                          whiteSpace: 'pre-wrap',
                          '& a': { color: '#7cc7ff' },
                          '& blockquote': {
                            my: 1,
                            ml: 0,
                            pl: 1.25,
                            borderLeft: '3px solid rgba(124, 199, 255, 0.75)',
                            color: 'rgba(232, 244, 255, 0.9)',
                          },
                          '& code, & pre': {
                            px: 0.5,
                            borderRadius: 0.75,
                            bgcolor: 'rgba(255,255,255,0.12)',
                          },
                          '& .tg-spoiler': {
                            borderRadius: 0.5,
                            bgcolor: 'rgba(232,244,255,0.35)',
                            color: 'transparent',
                          },
                        }}
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                      />
                    )}
                    {form.media_preview_url ? (
                      <Box component="img" src={form.media_preview_url} alt="" sx={{ width: '100%', display: 'block' }} />
                    ) : form.media_key ? (
                      <BotPostMediaImage mediaKey={form.media_key} alt={form.title || t('admin.botPosts.botPostAlt')} />
                    ) : null}
                    {(!form.media_key || !form.show_caption_above_media) && (
                      <Box
                        sx={{
                          p: 1.5,
                          whiteSpace: 'pre-wrap',
                          '& a': { color: '#7cc7ff' },
                          '& blockquote': {
                            my: 1,
                            ml: 0,
                            pl: 1.25,
                            borderLeft: '3px solid rgba(124, 199, 255, 0.75)',
                            color: 'rgba(232, 244, 255, 0.9)',
                          },
                          '& code, & pre': {
                            px: 0.5,
                            borderRadius: 0.75,
                            bgcolor: 'rgba(255,255,255,0.12)',
                          },
                          '& .tg-spoiler': {
                            borderRadius: 0.5,
                            bgcolor: 'rgba(232,244,255,0.35)',
                            color: 'transparent',
                          },
                        }}
                        dangerouslySetInnerHTML={{ __html: previewHtml || '&nbsp;' }}
                      />
                    )}
                    {form.include_button && (
                      <Box sx={{ p: 1 }}>
                        <Button fullWidth variant="contained" color="success" size="small">
                          {form.button_text || t('admin.botPosts.defaultButtonText')}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('admin.botPosts.history')}
              </Typography>
              {isLoading ? (
                <CircularProgress size={24} />
              ) : error ? (
                <Alert severity="error">{t('admin.botPosts.loadError')}</Alert>
              ) : visiblePosts.length === 0 ? (
                <Alert severity="info">{t('admin.botPosts.empty')}</Alert>
              ) : (
                <Stack spacing={1.25}>
                  {visiblePosts.map((post) => (
                    <Card key={post.id}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                              {post.title}
                            </Typography>
                            <Chip
                              size="small"
                              color={STATUS_COLORS[post.status]}
                              label={statusLabel(post.status)}
                            />
                          </Stack>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {stripTelegramHtml(post.body_html)}
                          </Typography>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            <Chip size="small" variant="outlined" label={postAudienceLabel(post)} />
                            {post.media_key && <Chip size="small" variant="outlined" label={t('admin.botPosts.imageChip')} />}
                            {post.button_text && <Chip size="small" variant="outlined" label={post.button_text} />}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {t('admin.botPosts.created', { date: formatDateTime(post.created_at) })}
                            {post.sent_at ? ` | ${t('admin.botPosts.sentAt', { date: formatDateTime(post.sent_at) })}` : ''}
                          </Typography>
                          {post.delivery_total ? (
                            <Stack spacing={0.25}>
                              <Typography variant="caption" color="text.secondary">
                                {t('admin.botPosts.deliveries', {
                                  sent: post.delivery_sent ?? 0,
                                  failed: post.delivery_failed ?? 0,
                                  skipped: post.delivery_skipped ?? 0,
                                })}
                              </Typography>
                              {post.delivery_deleted || post.delivery_delete_failed ? (
                                <Typography variant="caption" color="text.secondary">
                                  {t('admin.botPosts.deletions', {
                                    deleted: post.delivery_deleted ?? 0,
                                    failed: post.delivery_delete_failed ?? 0,
                                  })}
                                </Typography>
                              ) : null}
                            </Stack>
                          ) : null}
                          <Stack direction="row" spacing={1}>
                            <Button size="small" onClick={() => loadPostIntoComposer(post)}>
                              {t('admin.botPosts.useDraft')}
                            </Button>
                            {post.status === 'draft' && (
                              <Button
                                size="small"
                                startIcon={<SendIcon />}
                                disabled={sendMutation.isPending}
                                onClick={() => {
                                  if (window.confirm(t('admin.botPosts.confirmSendDraft', {
                                    title: post.title,
                                    audience: postAudienceLabel(post),
                                  }))) {
                                    sendMutation.mutate(post.id);
                                  }
                                }}
                              >
                                {t('admin.botPosts.sendDraft')}
                              </Button>
                            )}
                            {((post.delivery_sent ?? 0) > 0 || (post.delivery_delete_failed ?? 0) > 0) && (
                              <Button
                                size="small"
                                color="error"
                                startIcon={deleteMutation.isPending ? <CircularProgress size={16} /> : <DeleteIcon />}
                                disabled={deleteMutation.isPending}
                                onClick={() => {
                                  if (window.confirm(t('admin.botPosts.confirmDeleteMessages', { title: post.title }))) {
                                    deleteMutation.mutate(post.id);
                                  }
                                }}
                              >
                                {deleteMutation.isPending
                                  ? t('admin.botPosts.deletingMessages')
                                  : t('admin.botPosts.deleteMessages')}
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
